import {FC, UIEvent, useEffect, useMemo, useRef, useState} from 'react'

type LookupOption = {
  id: string | number
  label: string
}

interface SearchableLookupFieldProps {
  label: string
  value: string
  options: LookupOption[]
  onChange: (value: string) => void
  onOptionSelect?: (option: LookupOption) => void
  onSearchTextChange?: (value: string) => void
  searchText?: string
  displayValue?: string
  placeholder?: string
  helperText?: string
  showHelperText?: boolean
  disabled?: boolean
  required?: boolean
  error?: string
  listId: string
  wrapperClassName?: string
  inputClassName?: string
  isLoading?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  noOptionsText?: string
  showLabel?: boolean
}

const SearchableLookupField: FC<SearchableLookupFieldProps> = ({
  label,
  value,
  options,
  onChange,
  onOptionSelect,
  onSearchTextChange,
  searchText: controlledSearchText,
  displayValue,
  placeholder,
  helperText,
  showHelperText = true,
  disabled,
  required,
  error,
  listId,
  wrapperClassName = 'col-md-6',
  inputClassName,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  noOptionsText = 'No matching items found',
  showLabel = true,
}) => {
  const [internalSearchText, setInternalSearchText] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputId = `${listId}-input`

  const searchText = controlledSearchText ?? internalSearchText
  const updateSearchText = (nextText: string) => {
    if (controlledSearchText === undefined) {
      setInternalSearchText(nextText)
    }
    onSearchTextChange?.(nextText)
  }

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        normalizedLabel: option.label.trim().toLowerCase(),
        normalizedId: String(option.id).trim().toLowerCase(),
      })),
    [options]
  )

  const selectedLabel = useMemo(() => {
    const matched = normalizedOptions.find((option) => String(option.id) === String(value))
    if (matched?.label) {
      return matched.label
    }
    if (value && displayValue !== undefined) {
      return displayValue
    }
    return displayValue ?? ''
  }, [displayValue, normalizedOptions, value])

  useEffect(() => {
    if (isFocused) {
      return
    }

    if (value) {
      if (selectedLabel !== inputValue) {
        setInputValue(selectedLabel)
      }
      return
    }

    if (displayValue !== undefined && displayValue !== inputValue) {
      setInputValue(displayValue)
      return
    }

    if (displayValue === undefined && inputValue !== searchText) {
      setInputValue(searchText)
    }
  }, [displayValue, inputValue, isFocused, searchText, selectedLabel, value])

  const handleChange = (nextText: string) => {
    setInputValue(nextText)
    setIsFocused(true)
    updateSearchText(nextText)
    const normalized = nextText.trim().toLowerCase()
    if (!normalized) {
      onChange('')
      return
    }
    const matched = normalizedOptions.find(
      (option) =>
        option.normalizedLabel === normalized ||
        option.normalizedId === normalized
    )
    if (matched) {
      onChange(String(matched.id))
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    window.setTimeout(() => {
      setIsFocused(false)
      if (value) {
        setInputValue(selectedLabel)
        return
      }
      if (displayValue !== undefined) {
        setInputValue(displayValue)
        return
      }
      if (searchText.trim() === '') {
        setInputValue('')
      }
    }, 120)
  }

  const filteredOptions = useMemo(() => {
    const normalizedQuery = inputValue.trim().toLowerCase()
    if (!normalizedQuery) {
      return options
    }
    return normalizedOptions
      .filter(
        (option) =>
          option.normalizedLabel.includes(normalizedQuery) ||
          option.normalizedId.includes(normalizedQuery)
      )
      .map(({normalizedLabel, normalizedId, ...option}) => option)
  }, [inputValue, normalizedOptions, options])

  const showDropdown = !disabled && isFocused

  const handleOptionSelect = (option: LookupOption) => {
    onOptionSelect?.(option)
    onChange(String(option.id))
    setInputValue(option.label)
    setIsFocused(false)
  }

  const handleDropdownScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || isLoading) {
      return
    }
    const target = event.currentTarget
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24
    if (nearBottom) {
      onLoadMore()
    }
  }

  useEffect(() => {
    if (!isFocused) {
      return
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (wrapperRef.current?.contains(event.target as Node)) {
        return
      }
      setIsFocused(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isFocused])

  return (
    <div className={wrapperClassName} ref={wrapperRef}>
      {showLabel && (
        <label
          htmlFor={inputId}
          className={`form-label ${required ? 'required' : ''} fw-semibold fs-6 mb-2`}
          style={{color: '#181C32'}}
        >
          {label}
        </label>
      )}
      <div className='position-relative'>
        <input
          id={inputId}
          type='text'
          className={`form-control w-100 ${error ? 'is-invalid' : ''} ${inputClassName || ''}`.trim()}
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          data-list-id={listId}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete='off'
          style={{color: '#000'}}
        />
        {showDropdown && (
          <div
            className='position-absolute start-0 end-0 bg-white border rounded shadow-sm mt-1'
            style={{zIndex: 1055, maxHeight: '240px', overflowY: 'auto'}}
            onScroll={handleDropdownScroll}
            role='listbox'
            aria-label={label}
          >
            {filteredOptions.map((option) => (
              <button
                key={String(option.id)}
                type='button'
                className='dropdown-item text-wrap py-2'
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleOptionSelect(option)}
              >
                {option.label}
              </button>
            ))}
            {filteredOptions.length === 0 && !isLoading && (
              <div className='px-3 py-2 text-muted small'>{noOptionsText}</div>
            )}
            {isLoading && (
              <div className='px-3 py-2 text-muted small'>Loading…</div>
            )}
          </div>
        )}
      </div>
      {showHelperText && helperText && <div className='form-text small mt-1'>{helperText}</div>}
      {error && <div className='invalid-feedback d-block'>{error}</div>}
    </div>
  )
}

export default SearchableLookupField
